import { DEMO_PHONE } from '@/landing/content'
import { CtaBand, CtaRow, Crumbs, FaqList, Related, plain, type QA } from '@/site/content'
import { breadcrumbList, type Crumb, type PageMeta } from '@/site/meta'
import { SiteShell } from '@/site/SiteShell'

const TRAIL: Crumb[] = [{ name: 'Головна', path: '/' }, { name: 'Часті питання', path: '/faq' }]

const GROUPS: { title: string; items: QA[] }[] = [
  {
    title: 'Підключення',
    items: [
      {
        q: 'Як підключити Anytel?',
        a: 'Зареєструйтесь через email або Google, оберіть галузь, і майстер налаштування створить агента та сценарій дзвінка. Поговоріть з агентом з браузера, а коли все влаштовує, увімкніть переадресацію з робочого номера. Перший сценарій можемо налаштувати разом: залиште заявку на [безкоштовний пілот](/#pilot).',
      },
      {
        q: 'Чи треба міняти номер телефону?',
        a: 'Ні. Ви налаштовуєте переадресацію з наявного номера чи АТС на адресу Anytel або підключаєте свого SIP-оператора. Клієнти дзвонять туди ж, куди й раніше.',
      },
      {
        q: 'Скільки часу займає підключення?',
        a: 'Реєстрація і тестовий дзвінок займають кілька хвилин. Найбільше часу йде на базу знань: послуги, ціни, відповіді на часті питання. Зазвичай агента запускають за день.',
      },
      {
        q: 'Чи потрібне обладнання?',
        a: 'Ні. Агент працює в хмарі, оператори приймають дзвінки в браузері, а дзвінки приходять переадресацією з вашого номера чи АТС.',
      },
    ],
  },
  {
    title: 'Як працює агент',
    items: [
      {
        q: 'Якою мовою говорить агент?',
        a: 'Українською, англійською та іншими мовами. Голос, манеру і вимову налаштовуєте під свій бренд.',
      },
      {
        q: 'Чи розуміє агент суржик?',
        a: 'Агент розуміє живу розмовну мову, зокрема суржик і змішування української з російською: сучасні голосові моделі навчені на звичайному мовленні. Відповідає він тією мовою, яку ви задали. Перевірте на своїх фразах тестовим дзвінком або подзвоніть на [демо-лінію](tel:' + DEMO_PHONE + ').',
      },
      {
        q: 'Що, якщо клієнт хоче поговорити з людиною?',
        a: 'Агент переведе дзвінок на співробітника або в чергу операторів, які відповідають прямо з кабінету в браузері. Якщо всі зайняті чи офіс зачинено, агент прийме заявку, а система передзвонить у робочий час.',
      },
      {
        q: 'Що робить агент, якщо не знає відповіді?',
        a: 'Не вигадує. Він відповідає лише за вашою базою знань, а якщо відповіді там немає, каже, що уточнить, і передає питання людині.',
      },
      {
        q: 'Агент може записувати клієнтів у наш графік?',
        a: 'Так. У кабінеті є календарі для кожного майстра, лікаря чи кабінету з робочими годинами і вихідними. Агент перевіряє вільний час і записує, подвійних записів система не допускає. Через API календар доступний і вашим системам.',
      },
      {
        q: 'Чи працює агент у месенджерах?',
        a: 'Так. Той самий агент з тими самими знаннями відповідає в Telegram, Viber, Messenger та Instagram, а всі розмови зібрані в одному розділі кабінету.',
      },
    ],
  },
  {
    title: 'Дані й безпека',
    items: [
      {
        q: 'Що з даними наших клієнтів?',
        a: 'Записи розмов і дані клієнтів зберігаються в захищеному сховищі, дані кожної компанії ізольовані від інших, з’єднання шифруються. Співробітники бачать лише розділи, до яких ви дали доступ. Щодо даних ваших клієнтів ми працюємо як розпорядник: обробляємо їх лише для надання сервісу і не продаємо. Детальніше в [політиці конфіденційності](/privacy).',
      },
      {
        q: 'Чи законно записувати розмови й відповідати голосом AI?',
        a: 'Так, якщо абонента попереджено. На початку кожного дзвінка звучить коротке повідомлення про запис і віртуального асистента.',
      },
    ],
  },
  {
    title: 'Ціни й пробний період',
    items: [
      {
        q: 'Скільки це коштує?',
        a: 'Щомісячна абонплата за кабінет плюс хвилини розмов AI-агента. Тарифи і що входить у кожен на сторінці [цін](/tsiny).',
      },
      {
        q: 'Чи можна спершу спробувати?',
        a: 'Так. Пробний період без банківської картки: зареєструйтесь, налаштуйте агента і зробіть тестовий дзвінок з браузера. Тривалість і ліміти пробного періоду описані на сторінці [цін](/tsiny).',
      },
      {
        q: 'Що буде після пробного періоду?',
        a: '[TODO: що відбувається після пробного періоду: автоматичний перехід на тариф чи пауза до оплати]. Записи, сценарії й налаштування зберігаються.',
      },
    ],
  },
]

export const meta: PageMeta = {
  title: 'Часті питання про AI-агента для дзвінків | Anytel',
  description:
    'Відповіді на часті питання про Anytel: як підключити, чи треба міняти номер, чи розуміє агент суржик, що з даними клієнтів, скільки коштує.',
  schema: [
    {
      '@type': 'FAQPage',
      mainEntity: GROUPS.flatMap((g) => g.items).map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: plain(f.a) },
      })),
    },
    breadcrumbList(TRAIL),
  ],
}

export default function FaqPage() {
  return (
    <SiteShell>
      <main id="main" className="lp-page">
        <div className="lp-wrap">
          <Crumbs items={TRAIL} />
          <header className="lp-page-head">
            <p className="lp-eyebrow">Питання</p>
            <h1>Часті питання</h1>
            <p className="lp-page-lead">
              Як підключити агента, що з номером і даними клієнтів, скільки коштує. Не знайшли відповіді? Залиште
              заявку на <a href="/#pilot">безкоштовний пілот</a>, і ми розповімо все на дзвінку.
            </p>
            <CtaRow />
          </header>
        </div>
        {GROUPS.map((g) => (
          <section key={g.title} className="lp-wrap lp-page-faq">
            <h2>{g.title}</h2>
            <FaqList items={g.items} />
          </section>
        ))}
        <CtaBand />
        <Related paths={['/tsiny', '/funktsii', '/dlya']} />
      </main>
    </SiteShell>
  )
}
